/**
 * Canonical admin-1 (province / state / region) registry.
 *
 * Every lookup here is COUNTRY-SCOPED on purpose: the codes are only unique
 * within a country, and the merged country-less maps this replaces silently
 * mis-resolved the overlaps (US "WA" Washington vs AU "WA" Western Australia,
 * CA "NT" Northwest Territories vs AU "NT" Northern Territory, CA "YT" Yukon
 * vs FR "YT" Mayotte). Passing the country is what makes the collision
 * structurally impossible rather than a precedence accident.
 *
 * Data comes from `admin1-registry.generated.ts` (Natural Earth 10m admin-1).
 * Pure, no I/O, no node builtins — safe to import from middleware and edge
 * routes.
 */

import { ADMIN1_BY_COUNTRY } from "./admin1-registry.generated";
import { getCountryDisplayName } from "../types/region.type";

export type Admin1 = {
  code: string;
  name: string;
  localNames?: Record<string, string>;
  aliases?: string[];
  listOnly?: boolean;
};

/**
 * The value the search / region flows persist when the user opts out of a
 * subregion and wants nationwide results. Stored verbatim in `UserLocation`
 * and compared by string across the app, so it stays a literal.
 */
export const ALL_REGIONS_SENTINEL = "All regions";

/** Internal country codes the registry has admin-1 data for. */
export const ADMIN1_COUNTRY_CODES: readonly string[] = Object.keys(ADMIN1_BY_COUNTRY);

/**
 * True when no specific subregion is selected — nothing chosen yet, or the
 * user explicitly picked the whole country.
 */
export function isWholeCountry(region?: string | null): boolean {
  if (region === null || region === undefined) return true;
  const trimmed = region.trim();
  return trimmed === "" || trimmed === ALL_REGIONS_SENTINEL;
}

/** Internal country code: the app uses UK where ISO 3166-1 uses GB. */
function normalizeCountry(country: string): string {
  const code = country.trim().toUpperCase();
  return code === "GB" ? "UK" : code;
}

/**
 * NFD-strip + case-fold — the same normalization the legacy
 * `findRegionCodeByName` used, so "Québec" and "quebec" both land on QC.
 */
function normalizeName(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/** ISO 639-1 primary subtag: accepts "fr" or a full locale like "fr-CA". */
function primaryLanguage(lang?: string): string | null {
  if (!lang) return null;
  const primary = lang.trim().toLowerCase().split(/[-_]/)[0];
  return primary || null;
}

const sortedCache = new Map<string, readonly Admin1[]>();
const lookupCache = new Map<string, Map<string, Admin1>>();

/**
 * Every admin-1 unit for a country, sorted by name. Nothing is filtered out —
 * `listOnly` units (no map geometry) are included and callers drop them if
 * they are rendering a map.
 */
export function getSubregionsForCountry(country: string): readonly Admin1[] {
  const key = normalizeCountry(country);
  const cached = sortedCache.get(key);
  if (cached) return cached;

  const units = ADMIN1_BY_COUNTRY[key];
  const sorted: readonly Admin1[] = units
    ? [...units].sort((a, b) => a.name.localeCompare(b.name))
    : [];
  sortedCache.set(key, sorted);
  return sorted;
}

/**
 * Fuzzy index for one country: normalized code / name / local name / alias to
 * unit. Built once per country, codes first so a code never loses to another
 * unit's alias that happens to share the spelling.
 */
function getLookup(country: string): Map<string, Admin1> {
  const key = normalizeCountry(country);
  const cached = lookupCache.get(key);
  if (cached) return cached;

  const units = getSubregionsForCountry(key);
  const index = new Map<string, Admin1>();
  const add = (raw: string | undefined, unit: Admin1): void => {
    if (!raw) return;
    const normalized = normalizeName(raw);
    // First writer wins: earlier passes are the more authoritative spellings.
    if (normalized && !index.has(normalized)) index.set(normalized, unit);
  };

  for (const unit of units) add(unit.code, unit);
  for (const unit of units) add(unit.name, unit);
  for (const unit of units) {
    for (const local of Object.values(unit.localNames ?? {})) add(local, unit);
  }
  for (const unit of units) {
    for (const alias of unit.aliases ?? []) add(alias, unit);
  }

  lookupCache.set(key, index);
  return index;
}

/**
 * Resolve any spelling of a subregion within a country: code, name, localized
 * name, or alias. This is the one place that absorbs the code-vs-name duality
 * `/api/detect-region` emits (Vercel sends the ISO code "QC", ipinfo sends the
 * full name "Quebec") plus the legacy Statistics Canada PRUIDs ("CA10").
 */
export function getSubregion(country: string, raw: string): Admin1 | null {
  if (!raw) return null;
  const units = getSubregionsForCountry(country);
  if (units.length === 0) return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  // 1. Code, case-insensitive.
  const upper = trimmed.toUpperCase();
  for (const unit of units) {
    if (unit.code === upper) return unit;
  }

  // 2. Exact name.
  for (const unit of units) {
    if (unit.name === trimmed) return unit;
  }

  // 3. Accent/case-insensitive name, localized name, or alias.
  return getLookup(country).get(normalizeName(trimmed)) ?? null;
}

/** Canonical code for a subregion name (or any other accepted spelling). */
export function findSubregionCode(country: string, name: string): string | null {
  return getSubregion(country, name)?.code ?? null;
}

/**
 * Display name for a subregion, localized when the registry carries a
 * translation. `lang` accepts a language code or a full locale ("fr-CA").
 */
export function getSubregionName(country: string, code: string, lang?: string): string | null {
  const unit = getSubregion(country, code);
  if (!unit) return null;

  const primary = primaryLanguage(lang);
  return (primary && unit.localNames?.[primary]) || unit.name;
}

/**
 * Region context string for AI prompts, e.g. "Quebec, Canada". Matches the
 * legacy `formatRegionForAI` output for the shipped countries; unlike the
 * legacy helper this one honours `lang` when a translation exists.
 */
export function formatSubregionForAI(country: string, code: string, lang?: string): string | null {
  const name = getSubregionName(country, code, lang);
  if (!name) return null;

  const countryName = getCountryDisplayName(country);
  if (!countryName) return null;

  return `${name}, ${countryName}`;
}

/**
 * ISO 3166-2 identifier, e.g. "CA-QC". The country half uses the ISO spelling,
 * so the internal UK becomes GB ("GB-SCT"). Unknown codes are passed through
 * uppercased rather than dropped, so the value stays round-trippable.
 */
export function toIso31662(country: string, code: string): string {
  const internal = normalizeCountry(country);
  const iso = internal === "UK" ? "GB" : internal;
  const unit = getSubregion(internal, code);
  return `${iso}-${unit ? unit.code : code.trim().toUpperCase()}`;
}

/** Whether the country knows this subregion under any accepted spelling. */
export function isValidSubregion(country: string, code: string): boolean {
  return getSubregion(country, code) !== null;
}
